import {
  isDocRef,
  isINamedDocument,
  isObject,
  ISoftDelete,
  Region,
  type DocumentReference,
  type IFirestoreModel,
  type INamedDocument,
  type Timestamp,
  type WithRef,
} from '@principle-theorem/shared';
import { type ChartView } from '../clinical-charting/core/chart-view';
import { type IPerioSettings } from '../clinical-charting/core/perio';
import { type InteractionType } from '../interaction/interaction';
import { type MentionResourceType } from '../mention/mention';
import { Gender } from '../patient/patient';
import { type IPractice } from '../practice/practice';
import { type ITag } from '../tag/tag';
import { type ITeam } from '../team/team';
import { type ITimelineStafferDisplayOptions } from '../timeline/interactive-timeline-display-options';
import { type IUser } from '../user/user';
import { type IQuickChartingConfigurations } from './quick-charting-configuration';
import { type IStafferMetadata } from './staffer-metadata';
import { TreatmentPlanViewType } from '../clinical-charting/treatment/treatment-plan';
import { type ReferralStatus, type ReferralType } from '../referral/referral';

export enum ServiceTypeModality {
  GeneralDentist = 'D',
  Endodontist = '3',
  OralSurgeon = '4',
  Orthodontist = '5',
  Paedodontist = '6',
  Periodontist = '7',
  Prosthetist = '8',
  Prosthodontist = '9',
  DentalTechnician = '0',
  Physiotherapist = 'P',
  NotApplicable = 'N/A',
}

export interface IProviderData {
  practiceRef: DocumentReference<IPractice>;
  providerNumber: string;
  providerModality: ServiceTypeModality;
  payeeProviderNumber?: string;
}

export interface IRecentlyViewed<T extends object = object> {
  title: string;
  ref: DocumentReference<T>;
  viewedAt: Timestamp;
  type: MentionResourceType;
}

export type ProviderLineItemDetails = Partial<IProviderData> & {
  user: INamedDocument<IUser>;
};

export interface IStafferOnlineProfile {
  name?: string;
  gender?: Gender;
  title?: string;
  bio?: string;
  profileImageURL?: string;
}

export enum BillingEntityRegistrationNumberType {
  ABN = 'ABN',
  ACN = 'ACN',
  NZBN = 'NZBN',
}

export const BILLING_ENTITY_REGISTRATION_NUMBER_REGION_MAP: {
  [key in Region]: BillingEntityRegistrationNumberType[];
} = {
  [Region.Australia]: [
    BillingEntityRegistrationNumberType.ABN,
    BillingEntityRegistrationNumberType.ACN,
  ],
  [Region.NewZealand]: [BillingEntityRegistrationNumberType.NZBN],
};

export interface IBillingEntityData {
  registrationNumberType: BillingEntityRegistrationNumberType;
  registrationNumber: string;
  address: string;
}

export interface IBillingEntity extends ISoftDelete {
  uid: string;
  isDefault: boolean;
  displayName: string;
  entity: IBillingEntityData;
}

export interface IStaffer extends IFirestoreModel {
  user: INamedDocument<IUser>;
  teams: INamedDocument<ITeam>[];
  tasks: IStafferTasks;
  providerDetails: IProviderData[];
  billingEntities?: IBillingEntity[];
  prescriberNumber?: string;
  qualifications?: string;
  quickChartingConfiguration: IQuickChartingConfigurations;
  providerCommission?: number;
  settings?: Partial<IStafferSettings>;
  recentlyViewed?: IRecentlyViewed[];
  onlineProfile?: IStafferOnlineProfile;
  metadata?: IStafferMetadata;
}

export function isStaffer(data: unknown): data is WithRef<IStaffer> {
  return (
    isObject(data) &&
    'ref' in data &&
    isDocRef(data.ref) &&
    'user' in data &&
    isINamedDocument(data.user)
  );
}

export interface IChartSettings {
  view: ChartView;
  perio: IPerioSettings;
}

export interface ITreatmentPlanSettings {
  filters: PlanStepPairStatus[];
  sortBy: SortStepOptions;
  groupedTreatments: boolean;
  planView: TreatmentPlanViewType;
}

export interface IReferralFilterSettings {
  status: ReferralStatus[];
  type: ReferralType[];
  needsActionOnly: boolean;
}

export interface IReferralSettings {
  filters: Partial<IReferralFilterSettings>;
}

export interface IStafferTimelineSettings
  extends ITimelineStafferDisplayOptions {
  filteredStaffByPractice: IStaffByPractice[];
  orderedStaffByPractice: IStaffByPractice[];
  showRosteredOffStaff: boolean;
  hideEmptyDays: boolean;
}

export interface IStaffByPractice {
  staff: DocumentReference<IStaffer>[];
  practice: DocumentReference<IPractice>;
}

export enum PatientMediaViewType {
  Grid = 'grid',
  List = 'list',
}

export enum PatientMediaSize {
  Small = 'small',
  Medium = 'medium',
  Large = 'large',
}

export enum PatientMediaGroupBy {
  None = 'none',
  Date = 'date',
  Tags = 'tags',
}

export enum PlanStepPairStatus {
  Completed = 'completed',
  InProgress = 'inProgress',
  Scheduled = 'scheduled',
  Unscheduled = 'unscheduled',
  Cancelled = 'cancelled',
}

export enum SortStepOptions {
  Ascending = 'ascending',
  Descending = 'descending',
}

export interface IStafferSettings {
  charting: Partial<IChartSettings>;
  interactions: Partial<{
    filters: InteractionType[];
    tagFilters: DocumentReference<ITag>[];
  }>;
  timeline: Partial<IStafferTimelineSettings>;
  display: Partial<{
    sidebarClosed: boolean;
  }>;
  conversations: Partial<{
    showAllPractices: boolean;
  }>;
  chats: Partial<{
    showAllPractices: boolean;
    notificationSoundOverride?: string;
  }>;
  notifications: Partial<{
    muteNotifications: boolean;
    patientArrivedSoundOverride?: string;
    dismissedInfoBoxes?: string[];
  }>;
  treatmentPlan: Partial<ITreatmentPlanSettings>;
  patientMedia: Partial<{
    viewType: PatientMediaViewType;
    size: PatientMediaSize;
    groupBy: PatientMediaGroupBy;
    imageEditMode: boolean;
  }>;
  referrals: Partial<IReferralSettings>;
  email: Partial<{
    saveAttachmentsToPatientFiles: boolean;
  }>;
}

export interface IStafferTasks {
  watching: number;
  assigned: number;
}

export enum StafferCollection {
  Snippets = 'snippets',
  FeeSchedules = 'feeSchedules',
  Notifications = 'notifications',
  PopupNotifications = 'popupNotifications',
  ConditionConfigurations = 'conditionConfigurations',
  TreatmentConfigurations = 'treatmentConfigurations',
  MultiTreatmentConfigurations = 'multiTreatmentConfigurations',
  RosterSchedules = 'rosterSchedules',
  ScribeLease = 'scribeLease',
}
